import { PickInfo } from 'deck.gl';
import { observable, action, runInAction } from 'mobx';
import { useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import { LngLat, LngLatBounds, ViewState } from 'react-map-gl';
import WebMercatorViewport, { Coordinates } from 'viewport-mercator-project';
import { MAP_DEFAULT_ZOOM, DEFAULT_VIEW_STATE_LOCATION, PinDropMode } from '..';
import { isPositionInViewingArea } from '../../../../helpers/isPositionInViewingArea';
import getBoundsFromVisitLocations, { BoundablePoint } from '../../../../helpers/visits/getBoundsFromVisitLocations';
import { VisitLocationDtoModelType, MapMarkerType } from '../../../../store/mst/models';
import { MapsRef } from '../../../shared/MapBase';
import getCurrentMapBounds from '../../../shared/MapBase/utils/getCurrentMapBounds';
import { IGeolocationDto } from '../../../../store/mst/models/RootStore.base';
import { OrderAttemptLocation } from '../mapHelperFunctions';
import { isValidCoordinate } from '../../../../helpers/validateCoordinates';

export interface LocalMapStoreProps {
  mapRef: React.RefObject<MapsRef>,
  locations: VisitLocationDtoModelType[] | undefined,
  selectedLocation?: VisitLocationDtoModelType,
  pinDropMode?: PinDropMode,
  geolocation?: IGeolocationDto,
  handlePinDrop?: (latlng: mapboxgl.LngLat, fixPrecision?: boolean) => void,
  hoverLocation?: (visitLocationId: string | null) => void,
  selectLocation?: (visitLocationId: string | null) => void,
  setPinDropMode?: (mode: PinDropMode) => void,
  orderAttemptLocations?: OrderAttemptLocation[],
}

// Helper function to get map view state from bounds
const getViewStateFromBounds = (
  mapBounds: LngLatBounds,
  reactMapGLRef: MapsRef['reactMapGLRef'],
  maxZoom: number,
  defaultZoom: number,
  bufferScale: number,
): { latitude: number; longitude: number; zoom: number } => {
  // Check if mapBounds is valid
  if (!mapBounds.getSouthWest || !mapBounds.getNorthEast) {
    throw new Error('Invalid map bounds');
  }

  const southWest = mapBounds.getSouthWest();
  const northEast = mapBounds.getNorthEast();

  // Check if we have valid bounds
  if (!isValidCoordinate(southWest.lng) || !isValidCoordinate(southWest.lat) ||
      !isValidCoordinate(northEast.lng) || !isValidCoordinate(northEast.lat)) {
    throw new Error('Invalid bound coordinates');
  }

  const sw: [number, number] = [southWest.lng, southWest.lat];
  const ne: [number, number] = [northEast.lng, northEast.lat];

  // Check for degenerate case (single point)
  if (Math.abs(sw[0] - ne[0]) < 0.000001 && Math.abs(sw[1] - ne[1]) < 0.000001) {
    // For a single point, just center on it with default zoom
    return {
      latitude: sw[1],
      longitude: sw[0],
      zoom: defaultZoom,
    };
  }

  const map = reactMapGLRef?.getMap();
  if (!map) throw new Error('Map not initialized');

  const width = (map.getContainer()?.clientWidth || 100) * bufferScale;
  const height = (map.getContainer()?.clientHeight || 100) * bufferScale;

  const boundedViewport = new WebMercatorViewport({ width, height })
    .fitBounds([sw, ne], {
      padding: 48,
    });

  return {
    latitude: boundedViewport.latitude,
    longitude: boundedViewport.longitude,
    zoom: Math.min(boundedViewport.zoom, maxZoom),
  };
};

const useLocalMapStore = ({
  mapRef,
  locations,
  selectedLocation,
  hoverLocation,
  selectLocation,
  geolocation,
  handlePinDrop,
  pinDropMode,
  setPinDropMode,
  orderAttemptLocations,
}: LocalMapStoreProps) => {

  const MAP_ERROR_ZOOM = 3;
  const BUFFER_SCALE = 0.90;
  const MAP_MAX_ZOOM = 16;
  const TRANSITION_DURATION = 300;

  // Calculate initial view state based on available data
  const getInitialViewState = () => {
    // Use selectedLocation if available
    if (selectedLocation && isValidCoordinate(selectedLocation.longitude) && isValidCoordinate(selectedLocation.latitude)) {
      return {
        latitude: selectedLocation.latitude!,
        longitude: selectedLocation.longitude!,
        zoom: MAP_DEFAULT_ZOOM,
      };
    }
    // Use first location if available
    if (locations && locations.length > 0) {
      const firstLoc = locations[0];
      if (isValidCoordinate(firstLoc.longitude) && isValidCoordinate(firstLoc.latitude)) {
        return {
          latitude: firstLoc.latitude!,
          longitude: firstLoc.longitude!,
          zoom: MAP_DEFAULT_ZOOM,
        };
      }
    }
    // Use geolocation if available
    if (geolocation && isValidCoordinate(geolocation.longitude) && isValidCoordinate(geolocation.latitude)) {
      return {
        latitude: geolocation.latitude,
        longitude: geolocation.longitude,
        zoom: MAP_DEFAULT_ZOOM,
      };
    }
    // Default fallback
    return {
      latitude: DEFAULT_VIEW_STATE_LOCATION.latitude,
      longitude: DEFAULT_VIEW_STATE_LOCATION.longitude,
      zoom: MAP_DEFAULT_ZOOM,
    };
  };

  const localMapStore = useLocalObservable(() => ({
    mapViewState: observable(getInitialViewState()),
    localPinMode: pinDropMode,
    isDraggingMap: false,
    reactMapGLRef: null as MapsRef['reactMapGLRef'],
    moveCoordinatesIntoView: action((longitude: number, latitude: number, newLocations: BoundablePoint[] | undefined) => {
      // Check if we have valid coordinates and a map reference
      if (!isValidCoordinate(longitude) || !isValidCoordinate(latitude) || !localMapStore.reactMapGLRef) {
        return;
      }

      try {
        const map = localMapStore.reactMapGLRef.getMap();
        if (!map) {
          return;
        }

        const mapWidth = map.getContainer()?.clientWidth || 100;
        const mapHeight = map.getContainer()?.clientHeight || 100;
        const viewport = new WebMercatorViewport({
          ...localMapStore.mapViewState,
          width: mapWidth,
          height: mapHeight,
        });

        let positionOnScreenInPixels: Coordinates;
        try {
          positionOnScreenInPixels = viewport.project([longitude, latitude]);
        } catch (error) {
          console.error('Error projecting coordinates:', { longitude, latitude }, error);
          return;
        }

        const bounds = getCurrentMapBounds(viewport, mapWidth, mapHeight);
        const isInViewingArea = isPositionInViewingArea({
          bounds,
          coordinates: [longitude, latitude],
          position: positionOnScreenInPixels,
          mapWidth,
          mapHeight,
          isLeftPanelOpen: false,
          isRightPanelOpen: false,
          isBottomPanelOpen: false,
          leftPanelWidth: 0,
          rightPanelWidth: 0,
          bottomPanelHeight: 0,
        });

        if (isInViewingArea || !newLocations?.length) {
          return;
        }

        const newBounds = getBoundsFromVisitLocations(newLocations);
        if (newBounds) {
          localMapStore.setMapBounds(newBounds);
        }
      } catch (error) {
        console.error('Error in moveCoordinatesIntoView:', error);
      }
    }),
    setMapBounds: action((mapBounds?: LngLatBounds | null) => {
      try {
        let viewState;

        if (mapBounds) {
          viewState = getViewStateFromBounds(mapBounds, localMapStore.reactMapGLRef, MAP_MAX_ZOOM, MAP_DEFAULT_ZOOM, BUFFER_SCALE);
        } else if (geolocation && isValidCoordinate(geolocation.latitude) && isValidCoordinate(geolocation.longitude)) {
          viewState = {
            latitude: geolocation.latitude,
            longitude: geolocation.longitude,
            zoom: MAP_DEFAULT_ZOOM,
          };
        } else {
          viewState = {
            latitude: DEFAULT_VIEW_STATE_LOCATION.latitude,
            longitude: DEFAULT_VIEW_STATE_LOCATION.longitude,
            zoom: MAP_DEFAULT_ZOOM,
          };
        }

        localMapStore.mapViewState = observable({
          ...viewState,
          duration: TRANSITION_DURATION,
        });
      } catch (error) {
        console.error('Error in setMapBounds:', error);
        // Set default view on error
        localMapStore.mapViewState = observable({
          latitude: DEFAULT_VIEW_STATE_LOCATION.latitude,
          longitude: DEFAULT_VIEW_STATE_LOCATION.longitude,
          zoom: MAP_DEFAULT_ZOOM,
          duration: TRANSITION_DURATION,
        });
      }
    }),
    handleViewStateChange: action(({ latitude, longitude, zoom }: ViewState) => {
      localMapStore.mapViewState = observable({ latitude, longitude, zoom });
    }),
    zoomIn: action(() => {
      const zoomInLevel = localMapStore.mapViewState.zoom ? Math.min(MAP_MAX_ZOOM, localMapStore.mapViewState.zoom + 1) : MAP_MAX_ZOOM;
      localMapStore.mapViewState = { ...localMapStore.mapViewState, zoom: zoomInLevel };
    }),
    zoomOut: action(() => {
      const zoomOutLevel = localMapStore.mapViewState.zoom ? Math.max(MAP_ERROR_ZOOM, localMapStore.mapViewState.zoom - 1) : MAP_ERROR_ZOOM;
      localMapStore.mapViewState = { ...localMapStore.mapViewState, zoom: zoomOutLevel };
    }),
    handleLocationHover: action((hovered: PickInfo<MapMarkerType>) => {
      if (localMapStore.localPinMode === PinDropMode.EDITING) {
        return;
      }

      if (hovered.object) {
        hoverLocation?.(hovered.object.properties.id);
      } else {
        hoverLocation?.(null);
      }
    }),
    handleLocationClick: action((clicked: PickInfo<MapMarkerType>) => {
      if (localMapStore.localPinMode === PinDropMode.EDITING) {
        return;
      }

      if (clicked.object?.properties.id) {
        selectLocation?.(clicked.object.properties.id);
      }
    }),
    updatePinDropMode: action((mode?: any) => {
      // input from toolbar click sends a mouse event object
      // ignore the input in this case and toggle between mode
      // based on local store
      if (Object.values(PinDropMode).includes(mode)) {
        localMapStore.localPinMode = mode;
        return;
      }

      switch (localMapStore.localPinMode) {
        case PinDropMode.VIEWING:
          localMapStore.localPinMode = PinDropMode.EDITING;
          break;
        case PinDropMode.EDITING:
          localMapStore.localPinMode = PinDropMode.VIEWING;
          break;
      }

    }),
    setPinDropCoordinates: action((pickInfo: PickInfo<any>) => {
      const allowPinDrop = !!handlePinDrop;
      if (allowPinDrop && localMapStore.localPinMode === PinDropMode.EDITING) {
        const { coordinate } = pickInfo;

        if (handlePinDrop && coordinate) {
          const [longitude, latitude] = coordinate;
          handlePinDrop({ lng: longitude, lat: latitude } as LngLat, true);
          localMapStore.updatePinDropMode(PinDropMode.VIEWING);
        }
      }
    }),
    setReactMapGLRef: action((ref: MapsRef['reactMapGLRef']) => {
      localMapStore.reactMapGLRef = ref;
    }),
  }));

  const validOrderAttemptLocations = orderAttemptLocations?.filter(
    (location) => isValidCoordinate(location.longitude) && isValidCoordinate(location.latitude),
  ) ?? [];

  const boundsLocations: BoundablePoint[] = [
    ...(locations ?? []),
    ...validOrderAttemptLocations,
  ];

  useEffect(() => {
    if (mapRef.current?.reactMapGLRef) {
      localMapStore.setReactMapGLRef(mapRef.current?.reactMapGLRef);

      // Set initial map bounds when the ref first becomes available
      // This fixes the race condition where locations are set before the map is ready
      if (boundsLocations.length > 0) {
        try {
          const bounds = getBoundsFromVisitLocations(boundsLocations);
          localMapStore.setMapBounds(bounds);
        } catch (error) {
          console.error('Error setting initial map bounds:', error);
        }
      }
    }
  }, [mapRef.current?.reactMapGLRef, locations, orderAttemptLocations]);

  useEffect(() => {
    const currentMapRef = mapRef.current?.reactMapGLRef || localMapStore.reactMapGLRef;
    if (currentMapRef && boundsLocations.length > 0) {
      if (!localMapStore.reactMapGLRef && mapRef.current?.reactMapGLRef) {
        localMapStore.setReactMapGLRef(mapRef.current.reactMapGLRef);
      }
      try {
        const bounds = getBoundsFromVisitLocations(boundsLocations);
        localMapStore.setMapBounds(bounds);
      } catch (error) {
        console.error('Error setting suggestion map bounds:', error);
      }
    }
  }, [locations, orderAttemptLocations]);

  useEffect(() => {
    if (selectedLocation &&
        isValidCoordinate(selectedLocation.longitude) &&
        isValidCoordinate(selectedLocation.latitude)) {
      // When selectedLocation changes, always update the view state to center on it
      // This ensures the map shows the correct location even on initial render
      // before the map ref is available
      if (localMapStore.reactMapGLRef) {
        // Map is ready - use proper bounds calculation
        try {
          if (boundsLocations.length > 0) {
            const bounds = getBoundsFromVisitLocations(boundsLocations);
            localMapStore.setMapBounds(bounds);
          }
        } catch (error) {
          console.error('Error setting map bounds for selected location:', error);
        }
      } else {
        // Map ref not ready yet - set view state directly to show correct location immediately
        runInAction(() => {
          localMapStore.mapViewState = observable({
            latitude: selectedLocation.latitude!,
            longitude: selectedLocation.longitude!,
            zoom: MAP_DEFAULT_ZOOM,
          });
        });
      }
    }
  }, [selectedLocation]);

  useEffect(() => {
    // to update local pin mode
    if (setPinDropMode && localMapStore.localPinMode) {
      setPinDropMode(localMapStore.localPinMode);
    }
  }, [localMapStore.localPinMode]);

  useEffect(() => {
    // to handle external triggers to enter into Edit mode
    if (pinDropMode !== localMapStore.localPinMode) {
      localMapStore.updatePinDropMode(pinDropMode);
    }
  }, [pinDropMode]);

  return localMapStore;
};

export default useLocalMapStore;
